import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useTypedSelector } from "../../hooks.tsx";
import {
  getAuthenticatedKeyPressHandler,
  getUnauthenticatedKeyPressHandler,
} from "./handleKeyPress.tsx";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { RootState } from "../../store/types.tsx";

interface SearchFieldProps {
  children?: React.ReactNode;
  onPulseAnimation?: () => void;
  stopPulseAnimation?: () => void;
}

const SearchField: React.FC<SearchFieldProps> = ({
  onPulseAnimation = () => {},
  stopPulseAnimation = () => {},
}) => {
  const dispatch = useDispatch();
  const clickedExample = useTypedSelector((state) => state.exampleState.text);
  const { globalSearchText, awaitingAskResponse } = useTypedSelector(
    (state) => state.searchFieldState,
  );
  const isAuthenticated = useTypedSelector(
    (state) => state.authState.isLoggedIn,
  );

  const selectedConversation = useTypedSelector(
    (state) => state.conversationState.selectedConversation,
  );
  const anonymousSession = useTypedSelector(
    (state) => state.authState.anonymousSession,
  );

  const selectedModel = useSelector(
    (state: RootState) => state.conversationState.selectedModel,
  );

  const [searchText, setSearchText] = useState<string>("");
  const [labelText, setLabelText] = useState<string>(
    i18n._(
      msg({
        message: "Ask me anything ...",
        context: "Label for an text input.",
      }),
    ),
  );
  const [searchInputError, setSearchInputError] = useState<boolean>(false);

  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));

  const fontSize = isSmallScreen
    ? "0.8rem"
    : isMediumScreen
      ? "0.9rem"
      : "1rem";

  useEffect(() => {
    if (searchText !== clickedExample) {
      setSearchText(clickedExample);
      setLabelText(" ");
    }
  }, [clickedExample]);

  useEffect(() => {
    setSearchText(globalSearchText);
    setLabelText(" ");
  }, [globalSearchText]);

  const stopAnimation = () => {
    if (stopPulseAnimation) stopPulseAnimation();
  };

  const handleKeyPress = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (isAuthenticated) {
        getAuthenticatedKeyPressHandler({
          searchText,
          setSearchText,
          setSearchInputError,
          onPulseAnimation,
          stopAnimation,
          dispatch,
          selectedConversation,
          selectedModel,
        })(event);
      } else {
        getUnauthenticatedKeyPressHandler({
          searchText,
          setSearchText,
          setSearchInputError,
          onPulseAnimation,
          stopAnimation,
          dispatch,
          anonymousSession,
        })(event);
      }
    },
    [
      isAuthenticated,
      searchText,
      selectedConversation,
      anonymousSession,
      dispatch,
    ],
  );

  const handleIconClick = () => {
    const fakeEvent = new KeyboardEvent("keydown", {
      key: "Enter",
      bubbles: true,
      cancelable: true,
    });

    handleKeyPress(fakeEvent as unknown as React.KeyboardEvent<HTMLDivElement>);
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setSearchText(event.target.value);
    if (event.target.value.trim() !== "") {
      setSearchInputError(false);
    }
    const { value } = event.target;
    setSearchText(value);
  };

  const handleFocus = (event: any) => {
    // Hide the label when the textbox is focused
    const labels = event.target.labels;
    if (labels && labels.length > 0) {
      setLabelText(" ");
    }
  };

  const handleBlur = (event: any): void => {
    // Show the label when the textbox is not focused
    const labels = event.target.labels;
    if (searchText.trim().length <= 0) {
      setLabelText(
        i18n._(
          msg({
            message: "Ask me anything ...",
            context: "Label for an text input.",
          }),
        ),
      );
      labels[0].style.visibility = "visible";
    }
  };

  return (
    <Box>
      <TextField
        size="small"
        sx={{
          m: { xs: "0% 0% 3% 0%" },
          backgroundColor: "white",
          overflow: "auto",
          borderRadius: "35px",
          width: { xs: "95%", sm: "90%", md: "90%", lg: "65%", xl: "65%" },
          pl: "4%",
          pr: "1%",
          pb: "0.1%",
          pt: "0%",
          textAlign: "left",
        }}
        maxRows={20}
        disabled={awaitingAskResponse}
        autoFocus
        onFocus={handleFocus}
        onBlur={handleBlur}
        label={labelText}
        variant="standard"
        multiline
        fullWidth
        value={searchText}
        onKeyDown={handleKeyPress}
        onChange={handleOnChange}
        InputProps={{
          endAdornment: (
            <IconButton
              onClick={handleIconClick}
              sx={{
                height: "0rem",
              }}
            >
              <ArrowUpwardIcon
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: "white",
                  borderRadius: "50%",
                  marginBottom: "35%",
                }}
                fontSize={"large"}
              />
            </IconButton>
          ),
          style: {
            textAlign: "center",
          },
          disableUnderline: true,
          sx: {
            // Give the container a fixed height
            minHeight: "1.8rem",
            fontSize: fontSize,
            // Target the multiline input
            "& .MuiInputBase-inputMultiline": {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: 0,
              paddingRight: "1rem",
              paddingBottom: "0.5rem",
              lineHeight: "1rem",
            },
          },
        }}
        InputLabelProps={{
          style: {
            marginLeft: "30px",
            marginTop: "3px",
            fontSize: fontSize,
            fontWeight: "bold",
            color: "rgba(105, 105, 105, 0.7)", // Medium grey with 70% opacity
          },
          sx: {
            transform: "translateY(50%)",
          },
        }}
        error={searchInputError}
      ></TextField>
    </Box>
  );
};

export default SearchField;
